import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Film, User, Mic, Play, Pause, Upload, Check, Clock, 
  ChevronRight, Users, FileText, Loader2, LogOut, Settings,
  Square, Volume2, Trash2, Clapperboard, Star, MessageSquare, Image as ImageIcon,
  Download, BookOpen
} from "lucide-react";
import { uploadBlobToS3 } from "@/utils/s3Upload";
import { getApiUrl } from '@/config/api';
import { getAssetUrl } from '@/utils/studioUtils';

const API = getApiUrl();

// Professional script formatter - formats script like industry-standard sides
const formatProfessionalScript = (script, characterNames = []) => {
  if (!script) return null;
  
  const lines = script.split('\n');
  const formatted = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    if (!trimmed) {
      formatted.push({ type: 'blank', content: '' });
      continue;
    }
    
    // Check for character name (all caps, possibly with parenthetical)
    const charMatch = trimmed.match(/^([A-Z][A-Z\s\'\-\.]+)(?:\s*\(([^)]+)\))?$/);
    if (charMatch && trimmed.length < 40 && !trimmed.includes(':')) {
      const charName = charMatch[1].trim();
      const parenthetical = charMatch[2];
      const isMyCharacter = characterNames.some(name => 
        charName.toLowerCase().includes(name.toLowerCase())
      );
      formatted.push({ 
        type: 'character', 
        content: charName, 
        parenthetical,
        highlight: isMyCharacter 
      });
      continue;
    }
    
    // Check for inline format "CHARACTER: dialogue"
    const inlineMatch = trimmed.match(/^([A-Z][A-Za-z\s\'\-\.]+):\s*(.+)$/);
    if (inlineMatch) {
      const charName = inlineMatch[1].trim().toUpperCase();
      const dialogue = inlineMatch[2].trim();
      const isMyCharacter = characterNames.some(name => 
        charName.toLowerCase().includes(name.toLowerCase())
      );
      formatted.push({ 
        type: 'character', 
        content: charName, 
        highlight: isMyCharacter 
      });
      formatted.push({ 
        type: 'dialogue', 
        content: dialogue, 
        highlight: isMyCharacter 
      });
      continue;
    }
    
    // Check for parenthetical direction (in parentheses)
    if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
      formatted.push({ type: 'parenthetical', content: trimmed });
      continue;
    }
    
    // Check for action/direction (starts with * or is in brackets)
    if (trimmed.startsWith('*') || trimmed.startsWith('[')) {
      formatted.push({ type: 'action', content: trimmed.replace(/^\*|\*$/g, '').replace(/^\[|\]$/g, '') });
      continue;
    }
    
    // Check if previous line was a character name - this is dialogue
    const prevFormatted = formatted[formatted.length - 1];
    if (prevFormatted && (prevFormatted.type === 'character' || prevFormatted.type === 'dialogue' || prevFormatted.type === 'parenthetical')) {
      formatted.push({ 
        type: 'dialogue', 
        content: trimmed,
        highlight: prevFormatted.highlight 
      });
      continue;
    }
    
    // Default to action/description
    formatted.push({ type: 'action', content: trimmed });
  }
  
  return formatted;
};

// Component to render professional script
const ProfessionalScript = ({ script, characterNames = [], className = "" }) => {
  const formatted = formatProfessionalScript(script, characterNames);
  
  if (!formatted) {
    return <p className="text-zinc-500 italic">No script for this scene</p>;
  }
  
  return (
    <div className={`font-['Courier_Prime',_'Courier_New',_monospace] text-[13px] leading-relaxed ${className}`}>
      {formatted.map((item, idx) => {
        switch (item.type) {
          case 'character':
            return (
              <div key={idx} className={`text-center mt-4 mb-1 ${item.highlight ? 'text-amber-400 font-bold' : 'text-zinc-300'}`}>
                {item.content}
                {item.parenthetical && <span className="text-zinc-500 font-normal"> ({item.parenthetical})</span>}
              </div>
            );
          case 'dialogue':
            return (
              <div key={idx} className={`mx-8 mb-2 ${item.highlight ? 'text-white bg-amber-500/10 px-2 py-1 rounded' : 'text-zinc-300'}`}>
                {item.content}
              </div>
            );
          case 'parenthetical':
            return (
              <div key={idx} className="text-center text-zinc-500 text-xs mb-1">
                {item.content}
              </div>
            );
          case 'action':
            return (
              <div key={idx} className="text-zinc-500 italic my-2 text-xs border-l-2 border-zinc-700 pl-3">
                {item.content}
              </div>
            );
          case 'blank':
            return <div key={idx} className="h-2" />;
          default:
            return <div key={idx} className="text-zinc-400">{item.content}</div>;
        }
      })}
    </div>
  );
};

export default function ActorPortal() {
  const navigate = useNavigate();
  const { user, token, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectScenes, setProjectScenes] = useState([]);
  const [loadingScenes, setLoadingScenes] = useState(false);
  const [allProjectCharacters, setAllProjectCharacters] = useState([]);
  
  // Full-page recording view state
  const [recordingViewScene, setRecordingViewScene] = useState(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  
  // Character profile modal state
  const [selectedCharacterProfile, setSelectedCharacterProfile] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Recording state
  const [recordingScene, setRecordingScene] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState("");
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const takeAudioRef = useRef(null);
  
  // PDF generation state
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'full' or 'sides'
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadType, setDownloadType] = useState(null); // 'sides' or 'full'
  const [downloadScope, setDownloadScope] = useState('all'); // 'all' or specific character id
  const [downloadOrganization, setDownloadOrganization] = useState('scene_order'); // 'scene_order' or 'by_character'
  
  // Open recording view for a scene
  const openRecordingView = (scene, index) => {
    setRecordingViewScene(scene);
    setCurrentSceneIndex(index);
    setRecordingScene(scene);
    setRecordedBlob(null);
    setRecordingTime(0);
  };
  
  // Navigate to next/previous scene in recording view
  const navigateScene = (direction) => {
    const newIndex = currentSceneIndex + direction;
    if (newIndex >= 0 && newIndex < projectScenes.length) {
      // Stop any recording in progress
      if (isRecording) {
        stopRecording();
      }
      const newScene = projectScenes[newIndex];
      setRecordingViewScene(newScene);
      setCurrentSceneIndex(newIndex);
      setRecordingScene(newScene);
      setRecordedBlob(null);
      setRecordingTime(0);
    }
  };
  
  // Close recording view
  const closeRecordingView = () => {
    if (isRecording) {
      stopRecording();
    }
    setRecordingViewScene(null);
    setRecordingScene(null);
    setRecordedBlob(null);
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/actor/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectScenes = async (projectId) => {
    setLoadingScenes(true);
    try {
      const response = await axios.get(`${API}/actor/projects/${projectId}/scenes`, {
        headers: getAuthHeaders()
      });
      setProjectScenes(response.data.scenes || []);
      setSelectedProject(response.data.project);
      setAllProjectCharacters(response.data.all_project_characters || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load scenes");
    } finally {
      setLoadingScenes(false);
    }
  };

  // Get the character name the actor is assigned to for this project
  const getAssignedCharacterName = () => {
    if (!selectedProject || !dashboardData?.assigned_characters) return null;
    const assignment = dashboardData.assigned_characters.find(
      item => item.project.id === selectedProject.id
    );
    return assignment?.character?.name || null;
  };
  
  // Get ALL characters the actor is assigned to for this project
  const getAssignedCharactersForProject = () => {
    if (!selectedProject || !dashboardData?.assigned_characters) return [];
    return dashboardData.assigned_characters
      .filter(item => item.project.id === selectedProject.id)
      .map(item => item.character);
  };
  
  // Open download dialog
  const openDownloadDialog = (type) => {
    setDownloadType(type);
    setDownloadScope('all');
    setDownloadOrganization('scene_order');
    setShowDownloadDialog(true);
  };

  // Generate PDF script for actor with options
  const handleGeneratePdfWithOptions = async () => {
    const characters = getAssignedCharactersForProject();
    if (characters.length === 0) {
      toast.error("Could not determine your characters for this project");
      return;
    }
    
    // Determine which character names to include
    let characterNames;
    if (downloadScope === 'all') {
      characterNames = characters.map(c => c.name);
    } else {
      const selectedChar = characters.find(c => c.id === downloadScope);
      characterNames = selectedChar ? [selectedChar.name] : [];
    }
    
    if (characterNames.length === 0) {
      toast.error("No characters selected");
      return;
    }
    
    setGeneratingPdf(true);
    setPdfType(downloadType);
    setShowDownloadDialog(false);
    
    try {
      const response = await axios.post(
        `${API}/projects/${selectedProject.id}/generate-script-pdf`,
        {
          character_names: characterNames,
          script_type: downloadType,
          organization: downloadOrganization
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.pdf_url) {
        window.open(getAssetUrl(response.data.pdf_url), '_blank');
        const charLabel = characterNames.length > 1 
          ? `${characterNames.length} characters` 
          : characterNames[0];
        toast.success(
          downloadType === 'sides' 
            ? `Your sides for ${charLabel} are ready!`
            : `Full script with ${charLabel}'s lines highlighted`
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
      setPdfType(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logged out");
  };

  // Recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const playRecording = () => {
    if (recordedBlob && audioRef.current) {
      const url = URL.createObjectURL(recordedBlob);
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setRecordingTime(0);
  };

  const uploadRecording = async () => {
    if (!recordedBlob || !recordingScene) return;
    
    setUploading(true);
    try {
      // Upload recording to S3 first
      const asset = await uploadBlobToS3(recordedBlob, selectedProject.id, 'recording.webm', {
        type: 'voice',
        name: `recording-${recordingScene.id}-${Date.now()}`
      });
      
      // Then notify the backend about the voice recording for this scene
      await axios.post(
        `${API}/actor/scenes/${recordingScene.id}/voice-from-s3`,
        {
          asset_id: asset.id,
          character_id: recordingScene.characters[0].id,
          file_url: asset.file_url
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Take uploaded! You can record another or close the dialog.");
      
      // Clear the recording but keep dialog open for another take
      setRecordedBlob(null);
      setRecordingTime(0);
      
      // Refresh scenes to show updated status
      if (selectedProject) {
        await fetchProjectScenes(selectedProject.id);
        // Also update the recording scene with fresh data
        const updatedScenes = projectScenes;
        const updatedScene = updatedScenes.find(s => s.id === recordingScene.id);
        if (updatedScene) {
          setRecordingScene(updatedScene);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload recording");
    } finally {
      setUploading(false);
    }
  };

  // Play a take
  const playTake = (track) => {
    if (playingTrackId === track.id) {
      takeAudioRef.current?.pause();
      setPlayingTrackId(null);
    } else {
      if (takeAudioRef.current) {
        const audioUrl = getAssetUrl(track.file_url);
        takeAudioRef.current.src = audioUrl;
        takeAudioRef.current.play().catch(err => {
          console.error('[ActorPortal] Audio playback failed:', err);
          toast.error("Failed to play audio");
          setPlayingTrackId(null);
        });
        setPlayingTrackId(track.id);
      }
    }
  };

  // Toggle favorite on a take
  const toggleFavorite = async (sceneId, track) => {
    const newValue = !track.is_favorite;
    
    // Optimistic update - update UI immediately
    setProjectScenes(prevScenes => prevScenes.map(scene => {
      if (scene.id === sceneId) {
        return {
          ...scene,
          audio_tracks: scene.audio_tracks.map(t => 
            t.id === track.id ? { ...t, is_favorite: newValue } : t
          )
        };
      }
      return scene;
    }));
    
    try {
      const response = await axios.patch(
        `${API}/actor/scenes/${sceneId}/audio-track/${track.id}`,
        { is_favorite: newValue },
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success(newValue ? "Marked as favorite" : "Removed from favorites");
    } catch (error) {
      console.error("Toggle favorite error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to update favorite");
      // Revert on error
      if (selectedProject) {
        await fetchProjectScenes(selectedProject.id);
      }
    }
  };

  // Save note on a take
  const saveNote = async (sceneId, trackId) => {
    try {
      await axios.patch(
        `${API}/actor/scenes/${sceneId}/audio-track/${trackId}`,
        { note: noteText },
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );
      setEditingNoteId(null);
      setNoteText("");
      toast.success("Note saved");
      // Refresh scenes
      if (selectedProject) {
        await fetchProjectScenes(selectedProject.id);
      }
    } catch (error) {
      console.error("Save note error:", error);
      toast.error("Failed to save note");
    }
  };

  // Delete a take
  const deleteTake = async (sceneId, trackId) => {
    if (!window.confirm("Are you sure you want to delete this take?")) return;
    
    try {
      await axios.delete(
        `${API}/actor/scenes/${sceneId}/audio-track/${trackId}`,
        { headers: getAuthHeaders() }
      );
      // Refresh scenes
      if (selectedProject) {
        fetchProjectScenes(selectedProject.id);
      }
      toast.success("Take deleted");
    } catch (error) {
      toast.error("Failed to delete take");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] p-4 md:p-8 animate-fadeIn">
      {/* Hidden audio elements for playback */}
      <audio ref={audioRef} className="hidden" />
      <audio 
        ref={takeAudioRef} 
        className="hidden" 
        onEnded={() => setPlayingTrackId(null)}
      />
      
      {/* Header */}
      <header className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Mic className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Actor Portal</h1>
              <p className="text-sm text-zinc-500">Welcome, {user?.name || user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/account")}
              className="text-zinc-400 hover:text-white"
              data-testid="actor-account-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Account
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-red-400"
              data-testid="actor-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto">
        {/* Back button when viewing project scenes */}
        {selectedProject && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedProject(null);
              setProjectScenes([]);
              setAllProjectCharacters([]);
            }}
            className="mb-4 text-zinc-400 hover:text-white"
            data-testid="back-to-projects-btn"
          >
            ← Back to Projects
          </Button>
        )}

        {!selectedProject ? (
          <>
            {/* Assigned Characters Overview */}
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Your Characters
              </h2>
              
              {dashboardData?.assigned_characters?.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="py-8 text-center">
                    <User className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No characters assigned yet</p>
                    <p className="text-sm text-zinc-500 mt-1">
                      Wait for a director to assign you to a character
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData?.assigned_characters?.map((item) => (
                    <Card 
                      key={item.character.id} 
                      className="bg-zinc-900/50 border-zinc-800 hover:border-amber-500/50 transition-colors"
                      data-testid={`character-card-${item.character.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                            {item.character.reference_images?.[0]?.file_url ? (
                              <img 
                                src={getAssetUrl(item.character.reference_images[0].file_url)}
                                alt={item.character.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="w-6 h-6 text-zinc-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-white truncate">
                              {item.character.name}
                            </h3>
                            <p className="text-sm text-zinc-500">
                              {item.project.name}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Projects */}
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Film className="w-5 h-5 text-indigo-400" />
                Your Projects
              </h2>
              
              {dashboardData?.projects?.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="py-8 text-center">
                    <Film className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No projects yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {dashboardData?.projects?.map((item) => (
                    <Card 
                      key={item.project.id} 
                      className="bg-zinc-900/50 border-zinc-800 hover:border-indigo-500/50 transition-colors cursor-pointer"
                      onClick={() => fetchProjectScenes(item.project.id)}
                      data-testid={`project-card-${item.project.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                              <Film className="w-6 h-6 text-indigo-400" />
                            </div>
                            <div>
                              <h3 className="font-medium text-white">{item.project.name}</h3>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-zinc-500 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {item.character_count} character{item.character_count !== 1 ? 's' : ''}
                                </span>
                                <span className="text-sm text-zinc-500 flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  {item.scene_count} scene{item.scene_count !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-zinc-500" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          /* Project Scenes View */
          <section>
            {/* Character Profiles Section - View Only */}
            {getAssignedCharactersForProject().length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Characters in This Project
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {/* Actor's own characters first */}
                  {getAssignedCharactersForProject().map(char => (
                    <button 
                      key={char.id} 
                      onClick={() => { setSelectedCharacterProfile(char); setSelectedImageIndex(0); }}
                      className="bg-gradient-to-b from-amber-500/10 to-zinc-900/50 border border-amber-500/30 rounded-lg p-3 text-center hover:border-amber-500/60 hover:from-amber-500/20 transition-all cursor-pointer"
                    >
                      <div className="w-16 h-16 mx-auto mb-2 rounded-full overflow-hidden bg-zinc-800 border-2 border-amber-500/50">
                        {char.reference_images?.[0]?.file_url ? (
                          <img 
                            src={getAssetUrl(char.reference_images[0].file_url)} 
                            alt={char.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-8 h-8 text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm font-medium text-white truncate">{char.name}</p>
                      <Badge className="mt-1 bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
                        Your Role
                      </Badge>
                    </button>
                  ))}
                  
                  {/* Other characters in project */}
                  {allProjectCharacters
                    .filter(char => !getAssignedCharactersForProject().find(c => c.id === char.id))
                    .map(char => (
                      <button 
                        key={char.id} 
                        onClick={() => { setSelectedCharacterProfile(char); setSelectedImageIndex(0); }}
                        className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 text-center hover:border-zinc-600 hover:bg-zinc-800/50 transition-all cursor-pointer"
                      >
                        <div className="w-16 h-16 mx-auto mb-2 rounded-full overflow-hidden bg-zinc-800">
                          {char.reference_images?.[0]?.file_url ? (
                            <img 
                              src={getAssetUrl(char.reference_images[0].file_url)} 
                              alt={char.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-medium text-zinc-300 truncate">{char.name}</p>
                      </button>
                    ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white">{selectedProject.name}</h2>
                <p className="text-sm text-zinc-500">
                  {projectScenes.length} scene{projectScenes.length !== 1 ? 's' : ''} with your character{getAssignedCharactersForProject().length > 1 ? 's' : ''}
                  {getAssignedCharactersForProject().length > 0 && (
                    <span className="text-amber-400">
                      {' '}• Playing {getAssignedCharactersForProject().map(c => c.name).join(', ')}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* PDF Script Buttons - Full Script first, then Sides */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDownloadDialog('full')}
                  disabled={generatingPdf}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  data-testid="download-full-script-btn"
                >
                  {generatingPdf && pdfType === 'full' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BookOpen className="w-4 h-4 mr-2" />
                  )}
                  Full Script
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDownloadDialog('sides')}
                  disabled={generatingPdf}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  data-testid="download-sides-btn"
                >
                  {generatingPdf && pdfType === 'sides' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  My Sides
                </Button>
                {dashboardData?.user?.can_see_full_script && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 ml-2">
                    Full Script Access
                  </Badge>
                )}
              </div>
            </div>

            {loadingScenes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : projectScenes.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center">
                  <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No scenes with your character yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {projectScenes.map((scene, index) => (
                  <Card 
                    key={scene.id} 
                    className="bg-zinc-900/50 border-zinc-800"
                    data-testid={`scene-card-${scene.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                          Scene {scene.order + 1}: {scene.name}
                          {scene.audio_tracks?.length > 0 && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                              <Check className="w-3 h-3 mr-1" />
                              {scene.audio_tracks.length} take{scene.audio_tracks.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </CardTitle>
                        <Button
                          size="sm"
                          onClick={() => openRecordingView(scene, index)}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid={`record-btn-${scene.id}`}
                        >
                          <Mic className="w-4 h-4 mr-2" />
                          {scene.audio_tracks?.length > 0 ? 'Add Take' : 'Record'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Animation Video or Preview Composite */}
                      {(scene.animation_url || scene.preview_composite_url) && (
                        <div className="mb-4 rounded-lg overflow-hidden border border-zinc-700 bg-black">
                          {scene.animation_url ? (
                            <video
                              src={getAssetUrl(scene.animation_url)}
                              controls
                              className="w-full max-h-64 object-contain"
                              poster={scene.preview_composite_url ? getAssetUrl(scene.preview_composite_url) : undefined}
                            />
                          ) : (
                            <div className="relative">
                              <img
                                src={getAssetUrl(scene.preview_composite_url)}
                                alt="Shot preview"
                                className="w-full max-h-64 object-contain"
                              />
                              <div className="absolute bottom-2 left-2 bg-black/70 text-zinc-400 text-xs px-2 py-1 rounded flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Preview Composite
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Director's Vision - shown if animation not available yet */}
                      {scene.director_prompt && !scene.animation_url && (
                        <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium mb-2">
                            <Clapperboard className="w-4 h-4" />
                            Director&apos;s Vision
                          </div>
                          <p className="text-zinc-300 text-sm italic">
                            &quot;{scene.director_prompt}&quot;
                          </p>
                        </div>
                      )}
                      
                      {/* Professional Script Format */}
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <ProfessionalScript 
                          script={scene.script_segment} 
                          characterNames={scene.characters?.map(c => c.name) || []}
                        />
                      </div>
                      
                      {/* Takes Section */}
                      {scene.audio_tracks?.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-zinc-400">Your Takes</h4>
                          </div>
                          <div className="space-y-2">
                            {scene.audio_tracks.map((track, takeIndex) => (
                              <div 
                                key={track.id}
                                className={`p-3 rounded-lg border ${track.is_favorite ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-800/50 border-zinc-700'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Play button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => playTake(track)}
                                      className="w-8 h-8 p-0 rounded-full"
                                    >
                                      {playingTrackId === track.id ? (
                                        <Pause className="w-4 h-4" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                    </Button>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-white">Take {takeIndex + 1}</span>
                                        {track.is_favorite && (
                                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                        )}
                                      </div>
                                      <span className="text-xs text-zinc-500">
                                        {new Date(track.uploaded_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {/* Favorite button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleFavorite(scene.id, track)}
                                      className={`w-8 h-8 p-0 ${track.is_favorite ? 'text-amber-400' : 'text-zinc-500 hover:text-amber-400'}`}
                                      title={track.is_favorite ? "Remove from favorites" : "Mark as favorite"}
                                    >
                                      <Star className={`w-4 h-4 ${track.is_favorite ? 'fill-amber-400' : ''}`} />
                                    </Button>
                                    {/* Note button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditingNoteId(track.id);
                                        setNoteText(track.note || "");
                                      }}
                                      className={`w-8 h-8 p-0 ${track.note ? 'text-blue-400' : 'text-zinc-500 hover:text-blue-400'}`}
                                      title="Add/edit note"
                                    >
                                      <MessageSquare className={`w-4 h-4 ${track.note ? 'fill-blue-400/20' : ''}`} />
                                    </Button>
                                    {/* Delete button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteTake(scene.id, track.id)}
                                      className="w-8 h-8 p-0 text-zinc-500 hover:text-red-400"
                                      title="Delete take"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Show note if exists */}
                                {track.note && editingNoteId !== track.id && (
                                  <div className="mt-2 text-xs text-zinc-400 bg-zinc-900/50 p-2 rounded">
                                    <span className="text-blue-400">Note:</span> {track.note}
                                  </div>
                                )}
                                
                                {/* Note editing */}
                                {editingNoteId === track.id && (
                                  <div className="mt-2 space-y-2">
                                    <Textarea
                                      value={noteText}
                                      onChange={(e) => setNoteText(e.target.value)}
                                      placeholder="Add a note about this take..."
                                      className="bg-zinc-900 border-zinc-700 text-sm h-20"
                                    />
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          setEditingNoteId(null);
                                          setNoteText("");
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => saveNote(scene.id, track.id)}
                                        className="bg-blue-600 hover:bg-blue-700"
                                      >
                                        Save Note
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Full-Page Recording View (Mobile App Style) */}
      {recordingViewScene && (
        <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col">
          {/* Top Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-900/95 backdrop-blur">
            <Button
              variant="ghost"
              size="sm"
              onClick={closeRecordingView}
              className="text-zinc-400 hover:text-white"
            >
              ← Back
            </Button>
            <div className="text-center">
              <p className="text-xs text-zinc-500">Scene {currentSceneIndex + 1} of {projectScenes.length}</p>
              <p className="text-sm font-medium text-white">{recordingViewScene.name}</p>
            </div>
            <div className="w-16" /> {/* Spacer for balance */}
          </div>
          
          {/* Main Content - Scrollable */}
          <ScrollArea className="flex-1 p-4">
            <div className="max-w-lg mx-auto space-y-4">
              {/* Preview Video/Image */}
              {(recordingViewScene.animation_url || recordingViewScene.preview_composite_url) && (
                <div className="rounded-xl overflow-hidden border border-zinc-800 bg-black">
                  {recordingViewScene.animation_url ? (
                    <video
                      src={getAssetUrl(recordingViewScene.animation_url)}
                      controls
                      className="w-full aspect-video object-contain"
                      poster={recordingViewScene.preview_composite_url ? getAssetUrl(recordingViewScene.preview_composite_url) : undefined}
                    />
                  ) : (
                    <img
                      src={getAssetUrl(recordingViewScene.preview_composite_url)}
                      alt="Shot preview"
                      className="w-full aspect-video object-contain"
                    />
                  )}
                </div>
              )}
              
              {/* Director's Vision */}
              {recordingViewScene.director_prompt && (
                <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
                  <div className="flex items-center gap-2 text-indigo-400 text-xs font-medium mb-2">
                    <Clapperboard className="w-4 h-4" />
                    Director's Vision
                  </div>
                  <p className="text-zinc-200 text-sm italic">"{recordingViewScene.director_prompt}"</p>
                </div>
              )}
              
              {/* Script */}
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
                <p className="text-xs text-zinc-500 mb-3 flex items-center gap-2">
                  <FileText className="w-3 h-3" />
                  Your Lines
                </p>
                <div className="bg-zinc-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <ProfessionalScript 
                    script={recordingViewScene.script_segment} 
                    characterNames={recordingViewScene.characters?.map(c => c.name) || []}
                  />
                </div>
              </div>
              
              {/* Recording Area */}
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6">
                <div className="flex flex-col items-center gap-6">
                  {!recordedBlob ? (
                    <>
                      {/* Recording Button */}
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`w-28 h-28 rounded-full flex items-center justify-center transition-all shadow-lg ${
                          isRecording 
                            ? 'bg-red-500 animate-pulse shadow-red-500/30' 
                            : 'bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-amber-500/30'
                        }`}
                        data-testid={isRecording ? "stop-recording-btn" : "start-recording-btn"}
                      >
                        {isRecording ? (
                          <div className="text-center">
                            <div className="text-2xl font-mono text-white font-bold">{formatTime(recordingTime)}</div>
                            <Square className="w-4 h-4 mx-auto mt-1 text-white/80" />
                          </div>
                        ) : (
                          <Mic className="w-12 h-12 text-white" />
                        )}
                      </button>
                      <p className="text-sm text-zinc-400">
                        {isRecording ? 'Tap to stop' : 'Tap to record'}
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Playback Controls */}
                      <div className="flex items-center gap-6">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={isPlaying ? stopPlayback : playRecording}
                          className="w-16 h-16 rounded-full border-zinc-600 bg-zinc-800"
                          data-testid="play-recording-btn"
                        >
                          {isPlaying ? (
                            <Pause className="w-8 h-8" />
                          ) : (
                            <Play className="w-8 h-8" />
                          )}
                        </Button>
                      </div>
                      <p className="text-zinc-400 text-sm">Recording: {formatTime(recordingTime)}</p>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 w-full">
                        <Button
                          variant="outline"
                          onClick={discardRecording}
                          className="flex-1 border-zinc-700 text-zinc-400 hover:text-white"
                          data-testid="discard-recording-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Discard
                        </Button>
                        <Button
                          onClick={uploadRecording}
                          disabled={uploading}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          data-testid="upload-recording-btn"
                        >
                          {uploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Save Take
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Previous Takes */}
              {recordingViewScene.audio_tracks?.length > 0 && (
                <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
                  <p className="text-xs text-zinc-500 mb-3 flex items-center gap-2">
                    <Volume2 className="w-3 h-3" />
                    Previous Takes ({recordingViewScene.audio_tracks.length})
                  </p>
                  <div className="space-y-2">
                    {recordingViewScene.audio_tracks.map((track, i) => (
                      <div key={track.id} className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (playingTrackId === track.id) {
                              takeAudioRef.current?.pause();
                              setPlayingTrackId(null);
                            } else {
                              if (takeAudioRef.current) {
                                takeAudioRef.current.src = getAssetUrl(track.file_url);
                                takeAudioRef.current.play();
                                setPlayingTrackId(track.id);
                              }
                            }
                          }}
                          className="w-8 h-8 rounded-full"
                        >
                          {playingTrackId === track.id ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <div className="flex-1">
                          <p className="text-sm text-white">Take {i + 1}</p>
                          <p className="text-xs text-zinc-500">
                            {new Date(track.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                        {track.is_selected && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <Star className="w-3 h-3 mr-1" />
                            Selected
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tip */}
              <p className="text-xs text-zinc-500 text-center pb-4">
                💡 Find a quiet space and speak clearly into your microphone
              </p>
            </div>
          </ScrollArea>
          
          {/* Bottom Navigation Bar */}
          <div className="border-t border-zinc-800 bg-zinc-900/95 backdrop-blur p-4">
            <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={() => navigateScene(-1)}
                disabled={currentSceneIndex === 0}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30"
              >
                ← Previous
              </Button>
              <div className="text-center px-4">
                <div className="flex gap-1 justify-center">
                  {projectScenes.map((_, i) => (
                    <div 
                      key={i} 
                      className={`w-2 h-2 rounded-full ${i === currentSceneIndex ? 'bg-amber-500' : 'bg-zinc-700'}`}
                    />
                  )).slice(Math.max(0, currentSceneIndex - 2), Math.min(projectScenes.length, currentSceneIndex + 3))}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigateScene(1)}
                disabled={currentSceneIndex === projectScenes.length - 1}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30"
              >
                Next →
              </Button>
            </div>
          </div>
          
          {/* Hidden audio element for take playback */}
          <audio 
            ref={takeAudioRef} 
            onEnded={() => setPlayingTrackId(null)}
            className="hidden"
          />
        </div>
      )}
      
      {/* Character Profile Modal */}
      <Dialog open={!!selectedCharacterProfile} onOpenChange={(open) => {
        if (!open) {
          setSelectedCharacterProfile(null);
          setSelectedImageIndex(0);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-3">
              <User className="w-5 h-5 text-indigo-400" />
              {selectedCharacterProfile?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCharacterProfile && (
            <div className="space-y-4 py-4">
              {/* Main Image */}
              {selectedCharacterProfile.reference_images?.length > 0 && (
                <div className="space-y-3">
                  {/* Large primary image */}
                  <div className="rounded-xl overflow-hidden border border-zinc-700 bg-black">
                    <img
                      src={getAssetUrl(selectedCharacterProfile.reference_images[selectedImageIndex]?.file_url || selectedCharacterProfile.reference_images[0]?.file_url)}
                      alt={selectedCharacterProfile.name}
                      className="w-full max-h-80 object-contain"
                    />
                  </div>
                  
                  {/* Additional angles/images */}
                  {selectedCharacterProfile.reference_images.length > 1 && (
                    <div>
                      <p className="text-xs text-zinc-500 mb-2">All Angles ({selectedCharacterProfile.reference_images.length})</p>
                      <div className="grid grid-cols-4 gap-2">
                        {selectedCharacterProfile.reference_images.map((img, i) => (
                          <div 
                            key={img.asset_id || i} 
                            className={`aspect-square rounded-lg overflow-hidden border bg-black cursor-pointer transition-colors ${
                              i === selectedImageIndex 
                                ? 'border-amber-500 ring-2 ring-amber-500/30' 
                                : 'border-zinc-700 hover:border-amber-500/50'
                            }`}
                            onClick={() => setSelectedImageIndex(i)}
                          >
                            <img
                              src={getAssetUrl(img.file_url)}
                              alt={`${selectedCharacterProfile.name} angle ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Character Description if available */}
              {selectedCharacterProfile.description && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-sm text-zinc-300">{selectedCharacterProfile.description}</p>
                </div>
              )}
              
              {/* Character details */}
              <div className="flex flex-wrap gap-2">
                {selectedCharacterProfile.voice_type && (
                  <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                    Voice: {selectedCharacterProfile.voice_type}
                  </Badge>
                )}
                {selectedCharacterProfile.age && (
                  <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                    Age: {selectedCharacterProfile.age}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Download Options Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              {downloadType === 'sides' ? (
                <><Download className="w-5 h-5 text-indigo-400" /> Download Your Sides</>
              ) : (
                <><BookOpen className="w-5 h-5 text-indigo-400" /> Download Full Script</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Character Selection */}
            {getAssignedCharactersForProject().length > 1 && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-zinc-300">
                  Which characters do you want to include?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700 hover:bg-zinc-800/50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="downloadScope"
                      value="all"
                      checked={downloadScope === 'all'}
                      onChange={() => setDownloadScope('all')}
                      className="w-4 h-4 text-indigo-500"
                    />
                    <div>
                      <div className="font-medium text-white">All My Characters</div>
                      <div className="text-xs text-zinc-400">
                        {getAssignedCharactersForProject().map(c => c.name).join(', ')}
                      </div>
                    </div>
                  </label>
                  
                  {getAssignedCharactersForProject().map(char => (
                    <label 
                      key={char.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="radio"
                        name="downloadScope"
                        value={char.id}
                        checked={downloadScope === char.id}
                        onChange={() => setDownloadScope(char.id)}
                        className="w-4 h-4 text-indigo-500"
                      />
                      <div>
                        <div className="font-medium text-white">{char.name} only</div>
                        <div className="text-xs text-zinc-400">
                          {downloadType === 'sides' ? 'Just the scenes with this character' : 'Highlights for this character only'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Organization (only show if multiple characters selected) */}
            {downloadScope === 'all' && getAssignedCharactersForProject().length > 1 && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-zinc-300">
                  How should the script be organized?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700 hover:bg-zinc-800/50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="downloadOrganization"
                      value="scene_order"
                      checked={downloadOrganization === 'scene_order'}
                      onChange={() => setDownloadOrganization('scene_order')}
                      className="w-4 h-4 text-indigo-500"
                    />
                    <div>
                      <div className="font-medium text-white">By Scene Order</div>
                      <div className="text-xs text-zinc-400">
                        Scenes in shooting order — best for day-of reference and rehearsals
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700 hover:bg-zinc-800/50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="downloadOrganization"
                      value="by_character"
                      checked={downloadOrganization === 'by_character'}
                      onChange={() => setDownloadOrganization('by_character')}
                      className="w-4 h-4 text-indigo-500"
                    />
                    <div>
                      <div className="font-medium text-white">Grouped by Character</div>
                      <div className="text-xs text-zinc-400">
                        All scenes for each character together — best for line learning and character prep
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Single character message */}
            {getAssignedCharactersForProject().length === 1 && (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-sm text-zinc-300">
                  {downloadType === 'sides' ? (
                    <>Downloading sides for <span className="font-medium text-white">{getAssignedCharactersForProject()[0]?.name}</span> — your scenes with dialogue and action.</>
                  ) : (
                    <>Downloading full script with <span className="font-medium text-white">{getAssignedCharactersForProject()[0]?.name}</span>'s lines highlighted.</>
                  )}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleGeneratePdfWithOptions}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={generatingPdf}
            >
              {generatingPdf ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download PDF</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
